/**
 * useSubscriptionButton Hook
 *
 * Provides contextual button labels and actions based on user's subscription state
 *
 * Usage:
 * const { label, action, variant, disabled } = useSubscriptionButton();
 * <Button onClick={action}>{label}</Button>
 */

import { useSubscription } from '@/features/billing/contexts/SubscriptionContext';
import { useNavigate } from 'react-router-dom';

export type SubscriptionButtonContext =
  | 'free-trial'       // User on free trial (10 calls, 3 recordings)
  | 'pro-trial'        // User on pro trial (card on file, unlimited)
  | 'active'           // Active paid subscription
  | 'paused'           // Subscription paused
  | 'canceled'         // Subscription canceled
  | 'expired'          // Trial or subscription expired
  | 'no-subscription'; // No subscription at all

export interface SubscriptionButtonConfig {
  /**
   * Button label to display
   */
  label: string;

  /**
   * Short description (for tooltips/hints)
   */
  description: string;

  /**
   * Action to perform when button clicked
   */
  action: () => void;

  /**
   * Button variant (determines styling)
   */
  variant: 'default' | 'outline' | 'destructive' | 'secondary' | 'ghost';

  /**
   * Whether button should be disabled
   */
  disabled: boolean;

  /**
   * Current subscription context
   */
  context: SubscriptionButtonContext;

  /**
   * Icon name (optional, for UI libraries that support it)
   */
  icon?: string;
}

export function useSubscriptionButton(): SubscriptionButtonConfig {
  const { usage, loading } = useSubscription();
  const navigate = useNavigate();

  // Determine current context
  const getContext = (): SubscriptionButtonContext => {
    if (loading || !usage) return 'no-subscription';

    // Paused subscription
    if (usage.is_paused) return 'paused';

    // Canceled subscription
    if (usage.subscription_status === 'canceled') return 'canceled';

    // Active paid subscription
    if (usage.subscribed && usage.subscription_status === 'active' && !usage.isTrialActive) {
      return 'active';
    }

    // Pro trial (card on file, unlimited access)
    if (usage.isTrialActive && usage.subscribed && usage.trialType === 'stripe') {
      return 'pro-trial';
    }

    // Free trial (no card, limited access)
    if (usage.isTrialActive && !usage.subscribed && usage.trialType === 'app') {
      return 'free-trial';
    }

    // Trial expired
    if (!usage.isTrialActive && !usage.subscribed) {
      return 'expired';
    }

    return 'no-subscription';
  };

  const context = getContext();

  // Navigate to billing page
  const goToBilling = () => {
    navigate('/profile?tab=billing');
  };

  // Configure button based on context
  const configs: Record<SubscriptionButtonContext, Omit<SubscriptionButtonConfig, 'context'>> = {
    'free-trial': {
      label: 'Upgrade to Pro Trial',
      description: 'Add payment method for unlimited access during 15-day trial',
      action: goToBilling,
      variant: 'default',
      disabled: false,
      icon: 'sparkles',
    },
    'pro-trial': {
      label: 'Manage Subscription',
      description: `${usage?.trialDaysLeft || 15} days left in trial`,
      action: goToBilling,
      variant: 'outline',
      disabled: false,
      icon: 'settings',
    },
    'active': {
      label: 'Manage Subscription',
      description: 'View billing, invoices, and update payment method',
      action: goToBilling,
      variant: 'outline',
      disabled: false,
      icon: 'settings',
    },
    'paused': {
      label: 'Unpause Subscription',
      description: 'Resume your subscription to regain access',
      action: goToBilling,
      variant: 'default',
      disabled: false,
      icon: 'play',
    },
    'canceled': {
      label: 'Reactivate Subscription',
      description: 'Subscribe again to regain access',
      action: goToBilling,
      variant: 'default',
      disabled: false,
      icon: 'refresh-cw',
    },
    'expired': {
      label: 'Subscribe Now',
      description: 'Your trial has expired. Subscribe to continue.',
      action: goToBilling,
      variant: 'default',
      disabled: false,
      icon: 'credit-card',
    },
    'no-subscription': {
      label: 'Subscribe Now',
      description: 'Get access to all premium features',
      action: goToBilling,
      variant: 'default',
      disabled: false,
      icon: 'credit-card',
    },
  };

  const config = configs[context];

  return {
    ...config,
    context,
  };
}

/**
 * Get just the button label (convenience function)
 */
export function useSubscriptionButtonLabel(): string {
  const { label } = useSubscriptionButton();
  return label;
}

/**
 * Get limit reached message for upgrade prompts
 */
export function useSubscriptionLimitMessage(): {
  calls: string;
  recordings: string;
  shouldUpgrade: boolean;
} {
  const { usage } = useSubscription();
  const { context } = useSubscriptionButton();

  const shouldUpgrade = context === 'free-trial' || context === 'expired' || context === 'no-subscription';

  const callsMessage = usage?.calls_limit !== null
    ? `You've used ${usage?.calls_count || 0} of ${usage?.calls_limit} calls. Upgrade for unlimited calls.`
    : 'Unlimited calls available.';

  const recordingsMessage = usage?.recordings_limit !== null
    ? `You've used ${usage?.recording_count || 0} of ${usage?.recordings_limit} recordings. Upgrade for unlimited recordings.`
    : 'Unlimited recordings available.';

  return {
    calls: callsMessage,
    recordings: recordingsMessage,
    shouldUpgrade,
  };
}
